import Styles from '../../../2.jobPostings/edit/styles.module.css';
import Styless from '../../../styles.module.css';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { doc, getFirestore, onSnapshot, setDoc } from 'firebase/firestore';
import Navigation from './navigation/index';
import Info from '../../../2.jobPostings/edit/2.edit/2.info';
import Employment from '../../../2.jobPostings/edit/2.edit/5.20-employment';
import Benefit from '../../../2.jobPostings/edit/2.edit/8-benefit';
import ApplicationInfo from '../../../2.jobPostings/edit/2.edit/9.applicationInfo';
import HashTag from '../../../2.jobPostings/edit/2.edit/11.hashTag';
import Pamphlet from '../../../1.manual/3.school/2.edit/10.pamphlet';
import BackCaution from '../../../1.manual/3.school/backCaution/index';
import Summary from '../../../1.manual/3.school/2.edit/1.summary';
import Stores from '../../../3.recruit/2.edit/2.store';
import Preview from '../../../2.jobPostings/edit/preview/index';
import { defaultSummaryData, defaultInfo, defaultApplicationInfo, defaultPamphlet, defaultBenefit, defaultEmployment20, defaultEmployment18, defaultEmployment17, defaultHashTagsData, defaultTarget, formatSummary, formatInfo, formatApplicationInfo, formatPamphlet, formatBenefit, formatEmployment20, formatHashTags } from 'src/type';
import { useAppSelector } from 'src/salon/app/hook';
import { isMidCareerPositionSlug, midCareerJobPostingDocId, midCareerRecruitId } from '../../positions';

// 中途採用(スタイリスト/アシスタント)の「求人票の編集」画面。
// 新卒側(2.jobPostings/edit/index.tsx)の各セクション編集コンポーネントを
// そのまま再利用する。募集区分(Target)・18歳卒/17歳卒の雇用条件は
// 中途採用に該当しないため画面に含めない。
//
// 会社概要・PR(summary)・店舗情報の完了フラグ・パンフレットは
// 「求人の編集(中途)」と共有のデータのため、recruits/{companyId}_midCareer_{position}
// のフラットドキュメントを読み書きする(新卒側が_2027ドキュメントを共有するのと同じ構造)。
// 企業情報・雇用条件・福利厚生・応募情報・ハッシュタグは
// recruits/{companyId}/jobPostings/midCareer_{position} のサブコレクション
// ドキュメント(2027, 2028等と同じ場所)に保存する。
//
// 新卒側と異なり、「確定」ボタン・求人票PDF生成・検索インデックス登録とは
// 一切連携しない(アプリ側が中途採用に未対応のため)。

const MidCareerJobPostingEdit: React.FC = () => {
  const company = useAppSelector((state) => state.company.company);
  const db = getFirestore();

  const { position } = useParams<{ position: string }>();
  const companyId = company?.id ?? '';

  const navRef = useRef<HTMLDivElement>(null);
  const savedData = useRef<any>(null);

  const [navigation, setNavigation] = useState<number>(1);
  const [progress, setProgress] = useState<boolean[]>(Array(8).fill(false));
  const [changeData, setChangeData] = useState<boolean>(false);
  const [backCaution, setBackCaution] = useState<number>(0);

  const [summary, setSummary] = useState(defaultSummaryData);
  const memoizedSummary = useMemo(() => summary, [summary]);

  const [info, setInfo] = useState(defaultInfo);
  const memoizedInfo = useMemo(() => info, [info]);

  const [benefit, setBenefit] = useState(defaultBenefit);
  const memoizedBenefit = useMemo(() => benefit, [benefit]);

  const [employment20, setEmployment20] = useState(defaultEmployment20);
  const memoizedEmployment20 = useMemo(() => employment20, [employment20]);

  // 18歳卒/17歳卒は中途採用では使わないが、Employmentコンポーネントの
  // propの型上必要なため、既定値のまま保持する(読み書きは行わない)。
  const [employment18, setEmployment18] = useState(defaultEmployment18);
  const [employment17, setEmployment17] = useState(defaultEmployment17);

  const [applicationInfo, setApplicationInfo] = useState(defaultApplicationInfo);
  const memoizedApplicationInfo = useMemo(() => applicationInfo, [applicationInfo]);

  const [pamphlet, setPamphlet] = useState(defaultPamphlet);
  const memoizedPamphlet = useMemo(() => pamphlet, [pamphlet]);

  const [hashTag, setHashTag] = useState(defaultHashTagsData);
  const memoizedHashTag = useMemo(() => hashTag, [hashTag]);

  const [storeImageLocal, setStoreImageLocal] = useState<string>('');

  const [oldData, setOldData] = useState<any>(null);
  const [newData, setNewData] = useState<any>(null);

  const documentId = position && companyId && isMidCareerPositionSlug(position)
    ? midCareerRecruitId(companyId, position)
    : '';
  const jobPostingDocId = position && isMidCareerPositionSlug(position) ? midCareerJobPostingDocId(position) : '';

  // (1) summary / store / pamphlet ← recruits/{companyId}_midCareer_{position} (求人の編集と共有)
  useEffect(() => {
    if (!documentId || !companyId) return;

    setDoc(doc(db, 'recruits', documentId), {
      companyId,
      id: documentId,
      recruitStatus: 3,
    }, { merge: true }).catch((error) => {
      console.error('中途採用求人データの初期化に失敗しました:', error);
    });

    const unsubscribe = onSnapshot(doc(db, 'recruits', documentId), (docSnap) => {
      setOldData(docSnap.exists() ? docSnap.data() : {});
    });

    return () => unsubscribe();
  }, [documentId, companyId]);

  // (2) info / employment20 / benefit / applicationInfo / hashTag
  //     ← recruits/{companyId}/jobPostings/midCareer_{position} (2027, 2028と同じ場所)
  useEffect(() => {
    if (!companyId || !jobPostingDocId) return;

    const newRef = doc(db, 'recruits', companyId, 'jobPostings', jobPostingDocId);

    setDoc(newRef, {}, { merge: true }).catch((error) => {
      console.error('中途採用求人票データの初期化に失敗しました:', error);
    });

    const unsubscribe = onSnapshot(newRef, (docSnap) => {
      setNewData(docSnap.exists() ? docSnap.data() : {});
    });

    return () => unsubscribe();
  }, [companyId, jobPostingDocId]);

  useEffect(() => {
    if (oldData === null || newData === null) return;

    setProgress([
      oldData.summary?.isCompleted,
      newData.info?.isCompleted,
      oldData.store?.isCompleted,
      newData.employment20?.isCompleted,
      newData.benefit?.isCompleted,
      newData.applicationInfo?.isCompleted,
      oldData.pamphlet?.isCompleted,
      newData.hashTag?.isCompleted,
    ]);

    const formatted = {
      summary: formatSummary(oldData),
      pamphlet: formatPamphlet(oldData),
      info: formatInfo(newData),
      employment20: formatEmployment20(newData),
      applicationInfo: formatApplicationInfo(newData),
      benefit: formatBenefit(newData),
      hashTag: formatHashTags(newData),
    };

    savedData.current = formatted;

    setSummary(formatted.summary);
    setPamphlet(formatted.pamphlet);
    setInfo(formatted.info);
    setEmployment20(formatted.employment20);
    setApplicationInfo(formatted.applicationInfo);
    setBenefit(formatted.benefit);
    setHashTag(formatted.hashTag);
  }, [oldData, newData]);

  const resetToSaved = () => {
    if (!savedData.current) return;
    const d = savedData.current;
    setSummary(d.summary);
    setPamphlet(d.pamphlet);
    setInfo(d.info);
    setEmployment20(d.employment20);
    setApplicationInfo(d.applicationInfo);
    setBenefit(d.benefit);
    setHashTag(d.hashTag);
  };

  if (!position || !isMidCareerPositionSlug(position)) {
    return <Navigate to="/service/midCareer/jobPostings" replace />;
  }

  return (
    <>
    <div className={Styless.works_service}>
      <div>
        <div className={Styles.box}>

          <div ref={navRef} className={Styles.desktop_nav}>
            <Navigation
              progress={progress}
              changeData={changeData} navigation={navigation} setNavigation={setNavigation} setBackCaution={setBackCaution}
              companyId={companyId} documentId={documentId} jobPostingDocId={jobPostingDocId}
              published={newData?.jobPostingsStatus === 1} />
          </div>

          <div className={Styles.center}>
            {navigation === 1 ?
            <Summary documentId={documentId} setChangeData={setChangeData} summary={memoizedSummary} setSummary={setSummary} />
            : navigation === 2 ?
            <Info companyId={companyId} year={jobPostingDocId} setChangeData={setChangeData} info={memoizedInfo} setInfo={setInfo} />
            : navigation === 3 ?
            <Stores documentId={documentId} setStoreImageLocal={setStoreImageLocal} />
            : navigation === 4 ?
            <Employment
              graduateAge={20}
              companyId={companyId} year={jobPostingDocId} setChangeData={setChangeData}
              info={info}
              employment17={employment17} setEmployment17={setEmployment17}
              employment18={employment18} setEmployment18={setEmployment18}
              employment20={memoizedEmployment20} setEmployment20={setEmployment20} />
            : navigation === 5 ?
            <Benefit
              info={memoizedInfo}
              companyId={companyId} year={jobPostingDocId} setChangeData={setChangeData}
              benefit={memoizedBenefit} setBenefit={setBenefit}
              employment20={memoizedEmployment20} />
            : navigation === 6 ?
            <ApplicationInfo
              companyId={companyId} year={jobPostingDocId} setChangeData={setChangeData}
              info={info}
              applicationInfo={memoizedApplicationInfo} setApplicationInfo={setApplicationInfo} />
            : navigation === 7 ?
            <Pamphlet documentId={documentId} setChangeData={setChangeData} pamphlet={memoizedPamphlet} setPamphlet={setPamphlet} />
            : navigation === 8 ?
            <HashTag companyId={companyId} year={jobPostingDocId} setChangeData={setChangeData} hashTag={memoizedHashTag} setHashTag={setHashTag} />
            : <></>}

            <div style={{ height: '30px' }}></div>
          </div>

          <div className={Styles.desktop_preview}>
            <Preview
              navigation={navigation}
              documentId={documentId}
              storeImageLocal={storeImageLocal}
              summary={memoizedSummary}
              info={memoizedInfo}
              target={defaultTarget}
              benefit={memoizedBenefit}
              employment20={memoizedEmployment20}
              applicationInfo={memoizedApplicationInfo} />
          </div>
        </div>
      </div>
    </div>

    {backCaution !== 0 && (
      <BackCaution backCaution={backCaution} setBackCaution={setBackCaution} setNavigation={setNavigation} setChangeData={setChangeData} resetToSaved={resetToSaved} />
    )}
    </>
  );
};

export default MidCareerJobPostingEdit;
